import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Calendar,
  User,
  Building2,
  AlertCircle,
  Clock,
  MessageSquare,
  Send,
  Trash2,
  Edit,
  Loader2,
  ArrowRight,
  Circle,
  PlayCircle,
  CheckCircle2,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  useComentariosTarea,
  useAgregarComentario,
  useEliminarComentario,
  useHistorialAsignaciones,
  useMarcarComentariosLeidos,
} from '@/hooks/useTareas'
import { useCambiarEstadoTarea } from '@/core/hooks/useTareas'
import { tareasConFechaMovible, useActualizarFechaGrupo, useTareasDeGrupos } from '@/core/hooks/useTareasGrupo'
import { usePermissions } from '@/hooks/usePermissions'
import { handleMutationError, puedeTocarTarea } from '@/lib/permisos-guard'
import { SubtareasChecklist } from '@/components/tareas/SubtareasChecklist'
import { TareaRecordatorios } from '@/components/tareas/TareaRecordatorios'
import { TareaAdjuntos } from '@/components/tareas/TareaAdjuntos'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import { useConfirmarCompletar } from './ConfirmarCompletarDialog'
import { EditableDateCell } from './EditableDateCell'
import { AltaPendienteDeInscripcion, InscripcionBadge } from './Inscripcion'
import { ClaveFiscalDeInscripcion } from './ClaveFiscalInscripcion'
import { inscripcionSinAlta } from '@/core/lib/inscripcion'
import type { TareaCompleta, EstadoTarea } from '@/types/database'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { cn, formatearCUIT } from '@/lib/utils'
import { toast } from 'sonner'
import { useAuth0 } from '@auth0/auth0-react'

interface TareaDetalleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tarea: TareaCompleta | null
  onEditar?: (tarea: TareaCompleta) => void
  /**
   * ORR-327: el badge de estado es un control, no una etiqueta — cambia el estado
   * al toque. Ni este componente ni `useCambiarEstadoTarea` chequean permisos, asi
   * que el gate lo pone el caller. Default `true` para no alterar a los que ya
   * existian; los callers de solo consulta pasan `false`.
   */
  canChangeEstado?: boolean
}

const ESTADO_STYLES: Record<EstadoTarea, string> = {
  pendiente: 'bg-slate-100 text-slate-800',
  en_proceso: 'bg-blue-100 text-blue-800',
  completada: 'bg-green-100 text-green-800',
}

const ESTADO_LABELS: Record<EstadoTarea, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En Proceso',
  completada: 'Completada',
}

const ESTADO_ICONS: Record<EstadoTarea, typeof Circle> = {
  pendiente: Circle,
  en_proceso: PlayCircle,
  completada: CheckCircle2,
}

/**
 * Devuelve el foco al elemento que lo tenía cuando el panel se abrió. Radix solo lo devuelve a un
 * `SheetTrigger`, y estos paneles los abre el estado del caller, sin trigger.
 *
 * Si ese elemento ya no está en el documento o es el `body`, el foco va al primer elemento cuyo
 * atributo `respaldo.atributo` vale `respaldo.valor`. Es el caso del panel abierto desde un menú,
 * cuyo ítem se desmonta al cerrarse. `TareasLista` marca la fila de cada tarea con `data-tarea-id`,
 * y `TareaGrupoFila` marca el título del grupo con `data-grupo-clave`. Sin elemento de respaldo, el
 * foco sigue el comportamiento de Radix.
 */
interface RespaldoDeFoco {
  atributo: string
  valor: string
}

const buscarRespaldo = (respaldo: RespaldoDeFoco | null): HTMLElement | null => {
  if (!respaldo) return null
  const candidatos = document.querySelectorAll<HTMLElement>(`[${respaldo.atributo}]`)
  return Array.from(candidatos).find((el) => el.getAttribute(respaldo.atributo) === respaldo.valor) ?? null
}

const useDevolverFoco = (open: boolean, respaldo: RespaldoDeFoco | null) => {
  const previoRef = useRef<HTMLElement | null>(null)
  // Layout effect: corre antes de que Radix mueva el foco adentro del panel.
  useLayoutEffect(() => {
    if (open) previoRef.current = document.activeElement as HTMLElement | null
  }, [open])
  return (event: Event) => {
    const previo = previoRef.current
    const previoSigue = !!previo && previo !== document.body && document.contains(previo)
    const destino = previoSigue ? previo : buscarRespaldo(respaldo)
    if (!destino) return
    event.preventDefault()
    destino.focus()
  }
}

export function TareaDetalleDialog({
  open,
  onOpenChange,
  tarea,
  onEditar,
  canChangeEstado = false,
}: TareaDetalleDialogProps) {
  const { user } = useAuth0()
  const resolveNombre = useUserNameResolver()
  const cambiarEstado = useCambiarEstadoTarea()
  const { confirmar, dialogo } = useConfirmarCompletar()
  const [nuevoComentario, setNuevoComentario] = useState('')
  const devolverFoco = useDevolverFoco(
    open,
    tarea ? { atributo: 'data-tarea-id', valor: tarea.id } : null
  )

  // Historial de asignaciones
  const { data: asignaciones = [] } = useHistorialAsignaciones(tarea?.id || '')

  // Comentarios
  const {
    data: comentarios = [],
    isLoading: loadingComentarios,
    isFetching: recargandoComentarios,
  } = useComentariosTarea(tarea?.id || '', open)
  const agregarComentario = useAgregarComentario()
  const eliminarComentario = useEliminarComentario()
  const { mutate: marcarLeidos } = useMarcarComentariosLeidos()
  const ultimoComentarioId = comentarios[comentarios.length - 1]?.id

  // Abrir una tarea con comentarios los marca leídos cuando termina de recargarlos.
  // Un comentario que llega con el panel abierto también los marca.
  useEffect(() => {
    if (!open || !tarea?.id || !ultimoComentarioId || recargandoComentarios) return
    marcarLeidos(tarea.id)
  }, [open, tarea?.id, ultimoComentarioId, recargandoComentarios, marcarLeidos])

  const handleAgregarComentario = async () => {
    if (!nuevoComentario.trim() || !tarea) return

    try {
      await agregarComentario.mutateAsync({
        tareaId: tarea.id,
        contenido: nuevoComentario.trim(),
      })
      setNuevoComentario('')
      toast.success('Comentario agregado')
    } catch (error) {
      toast.error('Error al agregar comentario')
      console.error(error)
    }
  }

  const handleEliminarComentario = async (comentarioId: string) => {
    if (!tarea) return

    try {
      await eliminarComentario.mutateAsync({
        id: comentarioId,
        tareaId: tarea.id,
      })
      toast.success('Comentario eliminado')
    } catch (error) {
      toast.error('Error al eliminar comentario')
      console.error(error)
    }
  }

  const handleCambiarEstado = (nuevoEstado: EstadoTarea) => {
    if (!tarea || tarea.estado === nuevoEstado) return
    confirmar(nuevoEstado, () =>
      cambiarEstado.mutate(
        { id: tarea.id, estado: nuevoEstado, estadoAnterior: tarea.estado as EstadoTarea },
        {
          onSuccess: () => toast.success(`Tarea movida a "${ESTADO_LABELS[nuevoEstado]}"`),
          onError: () => toast.error('Error al cambiar el estado'),
        }
      )
    )
  }

  if (!tarea) return null

  const fechaLimite = tarea.fecha_limite ? parseISO(tarea.fecha_limite) : null
  const fechaCreacion = tarea.created_at ? parseISO(tarea.created_at) : new Date()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0"
        onCloseAutoFocus={devolverFoco}
      >
        {dialogo}
        <SheetHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <SheetDescription className="sr-only">
            Detalle de la tarea con información, estado y comentarios
          </SheetDescription>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <SheetTitle className="text-xl">{tarea.titulo}</SheetTitle>
              <div className="flex items-center gap-2 mt-2">
                {tarea.prioridad === 'urgente' && (
                  <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-200">
                    Urgente
                  </Badge>
                )}
                {tarea.tipo === 'inscripcion' && <InscripcionBadge />}
                {/* ORR-327: el badge de estado ES el control que lo cambia. Sin permiso
                    se muestra igual, pero como etiqueta: sin menu y sin la affordance de
                    click, que si no prometeria una accion que la fila ya tiene vedada. */}
                {canChangeEstado ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                      >
                        <Badge
                          className={cn(
                            'text-xs cursor-pointer hover:opacity-80 transition-opacity',
                            ESTADO_STYLES[tarea.estado as EstadoTarea]
                          )}
                        >
                          {ESTADO_LABELS[tarea.estado as EstadoTarea]}
                        </Badge>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {(['pendiente', 'en_proceso', 'completada'] as EstadoTarea[]).map((estado) => {
                        const Icon = ESTADO_ICONS[estado]
                        const isCurrentState = tarea.estado === estado
                        return (
                          <DropdownMenuItem
                            key={estado}
                            onClick={() => handleCambiarEstado(estado)}
                            className={cn(isCurrentState && 'bg-muted')}
                          >
                            <Icon
                              className={cn(
                                'mr-2 h-4 w-4',
                                estado === 'pendiente' && 'text-slate-500',
                                estado === 'en_proceso' && 'text-blue-500',
                                estado === 'completada' && 'text-green-500'
                              )}
                            />
                            {ESTADO_LABELS[estado]}
                            {isCurrentState && (
                              <span className="ml-auto text-xs text-muted-foreground">
                                actual
                              </span>
                            )}
                          </DropdownMenuItem>
                        )
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Badge className={cn('text-xs', ESTADO_STYLES[tarea.estado as EstadoTarea])}>
                    {ESTADO_LABELS[tarea.estado as EstadoTarea]}
                  </Badge>
                )}
              </div>
            </div>
            {onEditar && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditar(tarea)}
              >
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </Button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="space-y-6 pb-6">
            {inscripcionSinAlta(tarea) && <AltaPendienteDeInscripcion tarea={tarea} />}

            {tarea.tipo === 'inscripcion' && tarea.cuit && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">CUIT a inscribir</h4>
                <p className="text-sm font-medium">{formatearCUIT(tarea.cuit)}</p>
              </div>
            )}

            {tarea.tipo === 'inscripcion' && <ClaveFiscalDeInscripcion tarea={tarea} />}

            {/* Descripción */}
            {tarea.descripcion && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Descripción
                </h4>
                <p className="text-sm whitespace-pre-wrap">{tarea.descripcion}</p>
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4">
              {/* Cliente */}
              {tarea.cliente_nombre && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <p className="text-sm font-medium">{tarea.cliente_nombre}</p>
                  </div>
                </div>
              )}

              {/* Vencimiento fiscal */}
              {tarea.tipo_impuesto && (
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Vencimiento</p>
                    <p className="text-sm font-medium">
                      {tarea.tipo_impuesto} - {tarea.vencimiento_periodo}
                    </p>
                  </div>
                </div>
              )}

              {/* Asignado — read only */}
              {tarea.asignado_a && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Asignado a</p>
                    <p className="text-sm font-medium">{resolveNombre(tarea.asignado_a)}</p>
                  </div>
                </div>
              )}

              {/* Fecha límite */}
              {fechaLimite && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Fecha límite</p>
                    <p className="text-sm font-medium">
                      {format(fechaLimite, "d 'de' MMMM, yyyy", { locale: es })}
                    </p>
                  </div>
                </div>
              )}

              {/* Creado */}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Creada</p>
                  <p className="text-sm font-medium">
                    {format(fechaCreacion, "d 'de' MMMM, yyyy", { locale: es })}
                  </p>
                </div>
              </div>

              {/* Creador */}
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Creada por</p>
                  <p className="text-sm font-medium">
                    {resolveNombre(tarea.created_by ?? '')}
                  </p>
                </div>
              </div>

              {/* Fecha completada */}
              {tarea.fecha_completada && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Completada</p>
                    <p className="text-sm font-medium">
                      {format(parseISO(tarea.fecha_completada), "d 'de' MMMM, yyyy", { locale: es })}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Subtareas / Checklist */}
            {/* ORR-386: la subtarea no tiene dueño propio — hereda `puedeTocarTarea` de la tarea
                padre, la misma regla que ya gatea `canChangeEstado` acá arriba. Reusar el valor
                evita duplicar el cálculo en los 3 callers externos (Tareas/Calendario/Cliente). */}
            <SubtareasChecklist tareaId={tarea.id} canEdit={canChangeEstado} />

            <Separator />

            {/* Recordatorios */}
            <TareaRecordatorios tareaId={tarea.id} />

            <Separator />

            {/* Archivos adjuntos */}
            <TareaAdjuntos tareaId={tarea.id} clienteId={tarea.cliente_id} />

            {/* Historial de asignaciones */}
            {asignaciones.length > 0 && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ArrowRight className="h-4 w-4" />
                    <h4 className="text-sm font-medium">Historial de asignaciones ({asignaciones.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {asignaciones.map((asig) => (
                      <div key={asig.id} className="flex items-start gap-2 text-xs text-muted-foreground border-l-2 border-primary/20 pl-3 py-1">
                        <div className="flex-1">
                          <span className="font-medium text-foreground">{resolveNombre(asig.asignado_por)}</span>
                          {' asignó a '}
                          <span className="font-medium text-foreground">{resolveNombre(asig.asignado_a)}</span>
                          {asig.motivo && <span className="italic"> — {asig.motivo}</span>}
                        </div>
                        <span className="shrink-0">{format(parseISO(asig.created_at), 'dd MMM HH:mm', { locale: es })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Comentarios */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="h-4 w-4" />
                <h4 className="text-sm font-medium">
                  Comentarios ({comentarios.length})
                </h4>
              </div>

              {/* Lista de comentarios */}
              <div className="space-y-3 mb-4">
                {loadingComentarios ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : comentarios.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay comentarios aún
                  </p>
                ) : (
                  comentarios.map((comentario) => (
                    <div
                      key={comentario.id}
                      className="bg-muted/50 rounded-lg p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">
                              {resolveNombre(comentario.autor)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {comentario.created_at && format(
                                parseISO(comentario.created_at),
                                "d MMM, HH:mm",
                                { locale: es }
                              )}
                            </span>
                          </div>
                          <p className="text-sm">{comentario.contenido}</p>
                        </div>
                        {/* Solo el autor puede eliminar */}
                        {comentario.autor === user?.email && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => handleEliminarComentario(comentario.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Nuevo comentario */}
              <div className="space-y-2">
                <Textarea
                  placeholder="Escribe un comentario..."
                  value={nuevoComentario}
                  onChange={(e) => setNuevoComentario(e.target.value)}
                  rows={3}
                  className="resize-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleAgregarComentario}
                    disabled={
                      !nuevoComentario.trim() || agregarComentario.isPending
                    }
                    size="sm"
                  >
                    {agregarComentario.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Enviar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

interface TareaGrupoDetalleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** `grupo_clave` del grupo abierto. */
  clave: string | null
  onVerTarea?: (tarea: TareaCompleta) => void
}

/**
 * Detalle de un grupo de tareas: una fila por cliente, con su estado y un control para marcarla
 * terminada. Lista las tareas del grupo que el usuario ve. Cada control respeta el permiso de su
 * tarea, con la misma regla que el tablero.
 */
export function TareaGrupoDetalleDialog({
  open,
  onOpenChange,
  clave,
  onVerTarea,
}: TareaGrupoDetalleDialogProps) {
  const { esAdmin, puede, emailUsuario } = usePermissions()
  const resolveNombre = useUserNameResolver()
  const cambiarEstado = useCambiarEstadoTarea()
  const actualizarFecha = useActualizarFechaGrupo()
  const { confirmar, dialogo } = useConfirmarCompletar()
  const { data: tareas = [], isLoading } = useTareasDeGrupos(clave ? [clave] : [])
  const devolverFoco = useDevolverFoco(
    open,
    clave ? { atributo: 'data-grupo-clave', valor: clave } : null
  )

  const primera = [...tareas].sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))[0]
  const porCliente = [...tareas].sort((a, b) =>
    (a.cliente_nombre ?? '').localeCompare(b.cliente_nombre ?? '', 'es')
  )
  const terminadas = tareas.filter((t) => t.estado === 'completada').length
  const fechasPendientes = [...new Set(tareas.filter((t) => t.estado !== 'completada').map((t) => t.fecha_limite))]
  const fechaMovible = tareasConFechaMovible(tareas, puede, emailUsuario).length > 0

  const marcarTerminada = (tarea: TareaCompleta, terminada: boolean) => {
    const estado: EstadoTarea = terminada ? 'completada' : 'pendiente'
    confirmar(estado, () =>
      cambiarEstado.mutate(
        { id: tarea.id, estado, estadoAnterior: tarea.estado as EstadoTarea },
        { onError: (error) => handleMutationError(error, 'Error al cambiar el estado') }
      )
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0"
        onCloseAutoFocus={devolverFoco}
      >
        {dialogo}
        <SheetHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <SheetTitle className="text-xl">{primera?.titulo ?? 'Tarea de grupo'}</SheetTitle>
          <SheetDescription>
            {terminadas} de {tareas.length} clientes terminados{esAdmin ? '' : ', de los que ves'}
          </SheetDescription>
          {tareas.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Fecha límite:</span>
              <EditableDateCell
                fechaLimite={fechasPendientes.length === 1 ? fechasPendientes[0] : null}
                estado={fechaMovible ? 'pendiente' : 'completada'}
                canEdit={fechaMovible}
                emptyLabel={fechasPendientes.length > 1 ? 'Varias fechas' : 'Sin fecha'}
                onGuardar={(fecha) => actualizarFecha.mutate({ tareas, fecha })}
              />
            </div>
          )}
        </SheetHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="space-y-4 pb-6">
            {primera?.descripcion && (
              <p className="text-sm whitespace-pre-wrap">{primera.descripcion}</p>
            )}

            {isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ul className="divide-y rounded-md border">
                {porCliente.map((tarea) => {
                  const terminada = tarea.estado === 'completada'
                  const cliente = tarea.cliente_nombre ?? 'Sin cliente'
                  return (
                    <li key={tarea.id} className="flex items-center gap-3 px-3 py-2">
                      <Checkbox
                        checked={terminada}
                        disabled={!puedeTocarTarea(tarea, puede, emailUsuario)}
                        onCheckedChange={(valor) => marcarTerminada(tarea, valor === true)}
                        aria-label={`Terminado: ${cliente}`}
                      />
                      <button
                        type="button"
                        onClick={() => onVerTarea?.(tarea)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <p className={cn('text-sm font-medium truncate', terminada && 'line-through text-muted-foreground')}>
                          {cliente}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {tarea.asignado_a ? resolveNombre(tarea.asignado_a) : 'Sin asignar'}
                          {tarea.fecha_limite &&
                            ` · ${format(parseISO(tarea.fecha_limite), "d 'de' MMM", { locale: es })}`}
                          {tarea.fecha_completada &&
                            ` · completada el ${format(parseISO(tarea.fecha_completada), 'dd/MM/yyyy')}`}
                        </p>
                      </button>
                      <Badge className={cn('text-xs shrink-0', ESTADO_STYLES[tarea.estado as EstadoTarea])}>
                        {ESTADO_LABELS[tarea.estado as EstadoTarea]}
                      </Badge>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
